import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	FormsModule,
	ReactiveFormsModule,
	UntypedFormControl,
	ValidationErrors,
	ValidatorFn,
} from '@angular/forms';
import {
	MatAutocomplete,
	MatAutocompleteSelectedEvent,
	MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
	MatFormField,
	MatPrefix,
	MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'app-multi-select-typeahead',
	templateUrl: './multi-select-typeahead.component.html',
	styleUrls: ['./multi-select-typeahead.component.scss'],
	imports: [
		MatFormField,
		MatIcon,
		MatPrefix,
		MatInput,
		FormsModule,
		MatAutocompleteTrigger,
		ReactiveFormsModule,
		MatAutocomplete,
		MatOption,
		MatCheckbox,
		MatSuffix,
		MatTooltip,
		AsyncPipe,
	],
})
export class MultiSelectTypeaheadComponent<T>
	implements OnInit, OnChanges, OnDestroy
{
	@Input() allDropdownOptions?: T[] = []; // this is the list of all the objects that can appear in the dropdown
	@Input() filterOnField = ''; // this is the name of the field this gets displayed in the dropdown and also the
	// one the list of objects is filtered on when the user types in the input box
	@Input() labelText = ''; // the text to use as the lable for the input box
	@Input() placeholderText = ''; // the place holder text to put in the input field

	@Input() showSelectionsInInputField = false; // If all the selected objects should be shown inside the input field

	@Input() showSelectionsBelowInputField = true; // If all the selected objects should be shown inside the input field
	@Input() selectedItemIconName = ''; // The icon to use when the selections are displayed

	@Input() showSelectedItemSubOption = false; // Tells if the selected objects displayed below the input field should
	// have a sub option checkbox
	@Input() subOptionFieldName = ''; // The name of the field that is used to populate the checkbox
	@Input() subOptionText = ''; // the text to display with the checkbox
	@Input() subOptionTooltip = ''; // the tooltip text to use with the checkbox

	@Input() showSelectedItemSubOption2 = false; // Tells if the selected objects displayed below the input field should
	// have a 2 sub option checkbox

	@Input() subOptionFieldName2 = ''; // The name of the field that is used to populate the checkbox
	@Input() subOptionText2 = ''; // the text to display with the checkbox

	@Input() showSelectedItemMessage = false; // Tells if the selected objects displayed below the input field should
	// be able to display information/warning text below them

	// The name of the field that should be checked to see if the information/warning text should be shown
	@Input() showMessageFieldName = '';
	@Input() showMessageText = ''; // the text to be showing

	@Input() initiallySelectedItemIds: any[] = []; // This array has the ID's of the dropdown options that should be selected initially
	@Input() initiallySelectedIdField = ''; // This tells what field in dropdown options should be used as the ID
	@Input() keepCurrentSelections = false; // This tells if any currently selected items should be kept selected
	@Input() resetTrigger: number = 0; // When this value changes, the component will reset (clear input and selections)

	// When the list of currently selected options is changed we emit this event to return the list of selected
	// options to the parent component
	@Output() selectedOptionsChangedEvent = new EventEmitter<Set<T>>();

	@Input() modelType!: <Type>({}) => Type;

	@Input() minCharactersForSearch = 0; // Default to 0 to maintain existing behavior for other uses

	@Input() isUserSelection = false; // New input to indicate if this is a user selection

	@Input() autoFocus = false; // New input to control whether the input should automatically focus

	@Input() isRequired = false;

	@ViewChild('searchOptionsInput')
	searchOptionsInput!: ElementRef<HTMLInputElement>;
	@ViewChild('searchOptionsInput', { read: MatAutocompleteTrigger })
	optionsAutoTrigger!: MatAutocompleteTrigger;

	filteredDropdownOptions: Observable<any[]> = new Observable<T[]>();

	searchOptionsCtrl = new UntypedFormControl();

	optionsList: Set<any> = new Set<T>();

	separatorKeysCodes: number[] = [ENTER, COMMA];

	searchValue = '';

	hasFocus = false;

	private previousResetTrigger: number = 0;
	private keepPanelOpenFrame: number | null = null;
	private isDestroyed = false;

	@Output() searchInputChanged = new EventEmitter<string>();

	constructor() {}

	ngOnInit(): void {
		this.applyRequiredValidator();

		// when we have new text in the text box we need to filter the values in the list based on that text
		this.filteredDropdownOptions = this.searchOptionsCtrl.valueChanges.pipe(
			startWith(''),
			map((value) => {
				return this.filterDropdownOptions(value);
			})
		);
	}

	ngOnDestroy(): void {
		this.isDestroyed = true;
		if (this.keepPanelOpenFrame != null) {
			cancelAnimationFrame(this.keepPanelOpenFrame);
			this.keepPanelOpenFrame = null;
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.isRequired) {
			this.applyRequiredValidator();
		}

		// Check if resetTrigger changed - if so, reset the component
		if (
			changes.resetTrigger &&
			this.resetTrigger !== this.previousResetTrigger
		) {
			this.previousResetTrigger = this.resetTrigger;
			this.resetComponent();
			return;
		}

		this.filteredDropdownOptions = this.searchOptionsCtrl.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterDropdownOptions(value))
		);

		this.searchOptionsCtrl.setValue('');

		this.bulkItemSelection();
	}

	resetComponent(): void {
		// Clear the input field
		this.searchOptionsCtrl.setValue('');
		if (this.searchOptionsInput?.nativeElement) {
			this.searchOptionsInput.nativeElement.value = '';
		}

		this.optionsList.clear();

		this.filteredDropdownOptions = this.searchOptionsCtrl.valueChanges.pipe(
			startWith(''),
			map((value) => this.filterDropdownOptions(value))
		);

		// Emit empty selection to parent
		this.selectedOptionsChangedEvent.emit(this.optionsList);
		this.updateSelectionValidity();
	}

	markAsTouched(): void {
		this.searchOptionsCtrl.markAsTouched();
		this.updateSelectionValidity();
	}

	onInputBlur(): void {
		this.hasFocus = false;
		if (this.isRequired) {
			this.searchOptionsCtrl.markAsTouched();
			this.updateSelectionValidity();
		}
	}

	private applyRequiredValidator(): void {
		if (this.isRequired) {
			this.searchOptionsCtrl.setValidators([this.selectionRequiredValidator()]);
		} else {
			this.searchOptionsCtrl.clearValidators();
		}
		this.updateSelectionValidity();
	}

	private selectionRequiredValidator(): ValidatorFn {
		return (): ValidationErrors | null => {
			return this.optionsList.size > 0 ? null : { required: true };
		};
	}

	private updateSelectionValidity(): void {
		this.searchOptionsCtrl.updateValueAndValidity({ emitEvent: false });
	}

	private clearSearchInput(): void {
		this.searchOptionsCtrl.setValue('');
		if (this.searchOptionsInput?.nativeElement) {
			this.searchOptionsInput.nativeElement.value = '';
		}
	}

	// this function will select all of the items passed in the item selection array, using the item selection field.
	// By deafult it will clear any existing selections unless the optional keepCurrentSelections parameter is set to true
	private bulkItemSelection(): void {
		if (
			this.allDropdownOptions &&
			this.allDropdownOptions?.length > 0 &&
			this.initiallySelectedItemIds &&
			this.initiallySelectedItemIds?.length > 0
		) {
			if (!this.keepCurrentSelections) {
				this.optionsList = new Set<T>();
			}

			this.allDropdownOptions?.forEach((option) => {
				if (
					this.initiallySelectedItemIds.includes(
						(option as Record<string, any>)[this.initiallySelectedIdField]
					)
				) {
					this.optionsList.add(option);
				}
			});

			// notify the parent component what options are selected
			this.selectedOptionsChangedEvent.emit(this.optionsList);
			this.updateSelectionValidity();
		}
	}

	private filterDropdownOptions(value: string): T[] {
		let filteredList: T[] = [];
		let filterValue = '';

		if (!this.allDropdownOptions) {
			return [];
		} else if (value == null || typeof value !== 'string') {
			filterValue = this.searchOptionsInput.nativeElement.value ?? '';
		} else {
			filterValue = value;
		}

		filterValue = filterValue?.toLowerCase() ?? '';

		// Emit search event when minimum characters are typed
		if (
			this.minCharactersForSearch > 0 &&
			filterValue.length >= this.minCharactersForSearch
		) {
			this.searchInputChanged.emit(filterValue);
		}

		// Only filter if we have options
		if (this.allDropdownOptions) {
			filteredList = this.allDropdownOptions.filter((option) => {
				const fieldValue = (option as Record<string, any>)[this.filterOnField];
				if (typeof fieldValue !== 'string') {
					return value === '';
				}
				return fieldValue.toLowerCase().includes(filterValue) || value === '';
			});
		}
		return filteredList;
	}

	// this function controls what is actually shown in the text box
	displayDropdownOptionFn = (option: T): string => {
		const currentText = this.searchOptionsInput?.nativeElement?.value;
		return currentText;
	};

	// removes a selected item and notifies the parent control of the change
	removeOption(option: T): void {
		if (!this.optionsList) {
			return;
		}

		this.optionsList.delete(option);

		this.selectedOptionsChangedEvent.emit(this.optionsList);
		this.updateSelectionValidity();
	}

	// adds a selected item to the list and notifies the parent component of the change
	optionSelected(event: MatAutocompleteSelectedEvent): void {
		if (this.optionsList.has(event.option.value)) {
			// the option is already in the list so we want to remove it (they unchecked the box)
			this.optionsList.delete(event.option.value);
		} else {
			// the option is NOT already in the list so we want to add it (they checked the box)
			this.optionsList.add(event.option.value);
		}

		// Keep panel open - prevent it from closing by immediately reopening if it closed
		// Material Autocomplete closes the panel by default after selection, but for multi-select
		// we want it to stay open so users can select multiple items
		if (this.keepPanelOpenFrame != null) {
			cancelAnimationFrame(this.keepPanelOpenFrame);
		}
		this.keepPanelOpenFrame = requestAnimationFrame((): void => {
			this.keepPanelOpenFrame = null;
			// Avoid ObjectUnsubscribedError when the view is torn down before rAF runs
			if (this.isDestroyed || !this.optionsAutoTrigger) {
				return;
			}
			this.optionsAutoTrigger.openPanel();
		});

		this.selectedOptionsChangedEvent.emit(this.optionsList);
		this.updateSelectionValidity();
	}

	openOrClosePanel(evt: any, trigger: MatAutocompleteTrigger): void {
		evt.stopPropagation();
		this.hasFocus = !this.hasFocus;
		if (trigger.panelOpen) {
			trigger.closePanel();
		} else {
			trigger.openPanel();
		}
	}

	// Add this method to extract just the name
	extractNameFromDisplayName(displayName: string): string {
		// Only attempt to extract name if this is a user selection
		if (!this.isUserSelection) {
			return displayName;
		}

		// The format is typically "FirstName LastName (email@example.com)"
		const match = displayName.match(/(.*?)\s*\(/);
		return match ? match[1].trim() : displayName;
	}
}
